import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { vsprintf } from 'sprintf-js';
import { States } from 'constants/states';
import { IRootActionTypes, IRootState } from 'constants/types';
import { AudioStatuses } from 'redux/reducers/audioReducer';
import {
  setAudioClear,
  setAudioCurrentIndex,
  setAudioCurrentTime,
  setAudioLoading,
  setAudioStatus,
  setAudioTotalTime,
} from 'redux/actions/audio/audioActions';
import { APILinks } from 'constants/api';
import axios, { AXIOS_CANCEL_TOKEN_ANCHOR_MESSAGE, getPayloadFromError } from 'utils/axios';
import axiosCore from 'axios';
import { BOOK_LISTEN_FORMAT } from 'constants/books';
import { localData } from 'utils/storage';
import { LocalData } from 'constants/storage';

interface IUseAudio {
  handlePlay: () => void
  handlePause: () => void
}

const useAudio = (): IUseAudio => {
  const dispatch = useDispatch<IRootActionTypes>();

  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const audio = audioRef.current;

  const audioState = useSelector((state: IRootState) => state?.[States.AUDIO]);

  const audioStatus = audioState?.status;
  const audioStatusIsStop = audioStatus === AudioStatuses.STOP;

  const audioData = audioState?.data;
  const audioId = audioData?.id;
  const audioList = audioData?.list || [];

  const audioTime = audioState?.time;
  const audioCurrentTime = audioTime?.current || 0;
  const audioPlaybackRate = audioTime?.playbackRate || 1;

  const audioCurrent = audioState?.current;
  const audioCurrentIndex = audioCurrent?.index || 0;

  const audioListLength = Array.isArray(audioList) ? audioList.length : 0;
  const audioListMaxIndex = audioListLength - 1;
  const currentItemId = audioList?.[audioCurrentIndex]?.abookFileId;
  const currentItemType: string = audioList?.[audioCurrentIndex]?.type || BOOK_LISTEN_FORMAT;

  const src = (currentItemId && currentItemType)
    ? vsprintf(`/${APILinks.BOOKS_CONTENT}`, [currentItemId, currentItemType])
    : null;

  const handleSetStatus = (status: AudioStatuses) => {
    dispatch(setAudioStatus(status));
  };
  const handlePlay = () => {
    audio.play()
      .then(() => {
        handleSetStatus(AudioStatuses.PLAY);
      })
      .catch(() => {});
  };
  const handlePause = () => {
    audio.pause();
    handleSetStatus(AudioStatuses.PAUSE);
  };
  const handleStop = () => {
    dispatch(setAudioClear());
    audio.pause();
    audio.preload = 'none';
  };

  useEffect(() => {
    if (audioId) {
      dispatch(setAudioCurrentIndex(0));
      dispatch(setAudioCurrentTime(0));
      dispatch(setAudioLoading(true));
    }
  }, [audioId]);

  useEffect(() => {
    if (src) {
      audio.src = src;
      audio.preload = 'auto';
      audio.load();
      dispatch(setAudioCurrentTime(0));
      if (audio.playbackRate !== audioPlaybackRate) {
        audio.playbackRate = audioPlaybackRate;
      }
    } else {
      handleStop();
    }
  }, [src]);

  useEffect(() => {
    if (audioCurrentIndex !== 0) {
      setTimeout(handlePlay, 0);
    }
  }, [audioCurrentIndex]);

  useEffect(() => {
    const time: number = audioCurrentTime || 0;
    if (Math.floor(audio.currentTime) !== time) {
      audio.currentTime = time;
    }
  }, [audioCurrentTime]);

  useEffect(() => {
    if (audio.playbackRate !== audioPlaybackRate) {
      audio.playbackRate = audioPlaybackRate;
    }
  }, [audioPlaybackRate]);

  useEffect(() => {
    const canplayListener = () => {
      dispatch(setAudioLoading(false));
    };
    const endedListener = () => {
      if (audioCurrentIndex < audioListMaxIndex) {
        dispatch(setAudioCurrentIndex(audioCurrentIndex + 1));
      } else {
        handleStop();
      }
    };
    const durationchangeListener = () => {
      dispatch(setAudioTotalTime(audio.duration || 0));
    };
    const timeupdateListener = () => {
      dispatch(setAudioCurrentTime(Math.floor(audio.currentTime || 0)));
    };
    const playingListener = () => {
      handleSetStatus(AudioStatuses.PLAY);
    };
    const pauseListener = () => {
      if (!audioStatusIsStop) {
        handleSetStatus(AudioStatuses.PAUSE);
      }
    };
    const ratechangeListener = () => {
      localData(LocalData.AUDIO_PLAYBACK_RATE).setValue(audio.playbackRate);
    };
    const errorListener = () => {
      if (src) {
        // Проверяем доступен ли аудиофайл
        const cancelTokenSource = axiosCore.CancelToken.source();
        axios()
          .get(src, { cancelToken: cancelTokenSource.token })
          .then(() => {
            // Файл отдаётся успешно, можно отменить запрос, тк нет необходимости в этих данных
            endedListener();
            cancelTokenSource.cancel(AXIOS_CANCEL_TOKEN_ANCHOR_MESSAGE);
          })
          .catch((er) => {
            getPayloadFromError(er, dispatch);
            handleSetStatus(AudioStatuses.STOP);
          });
      } else {
        endedListener();
      }
    };

    if (audioId) {
      audio.addEventListener('canplay', canplayListener);
      audio.addEventListener('playing', playingListener);
      audio.addEventListener('pause', pauseListener);
      audio.addEventListener('ended', endedListener);
      audio.addEventListener('durationchange', durationchangeListener);
      audio.addEventListener('timeupdate', timeupdateListener);
      audio.addEventListener('ratechange', ratechangeListener);
      audio.addEventListener('error', errorListener);
    }

    return () => {
      if (audioId) {
        audio.removeEventListener('canplay', canplayListener);
        audio.removeEventListener('playing', playingListener);
        audio.removeEventListener('pause', pauseListener);
        audio.removeEventListener('ended', endedListener);
        audio.removeEventListener('durationchange', durationchangeListener);
        audio.removeEventListener('timeupdate', timeupdateListener);
        audio.removeEventListener('ratechange', ratechangeListener);
        audio.removeEventListener('error', errorListener);
      }
    };
  }, [audioId, src, audioCurrentIndex, audioListMaxIndex, audioStatusIsStop]);

  return { handlePlay, handlePause };
};

export default useAudio;
